import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { Unsubscribe } from 'router5/dist/types/base';
import { IAccountDetailsDTO, IAccountsDAO } from '../../api/accounts.dao';
import { RouteParams, Routes } from '../../routes-table';
import { IRouter } from '../../infrastructure/router';
import { DeepReadonly } from '../../infrastructure/type-extensions';
import { IModuleVM } from '../../infrastructure/components/module-vm';
import { RADIX_PARAM } from '../../constants/common.constants';

enum EventsModulePage {
    Events,
    ManageEvent,
}

class EventsVM implements IModuleVM {
    @mobx.observable
    private account: IAccountDetailsDTO;
    @mobx.observable
    private eventModulePage: EventsModulePage;
    @mobx.observable
    private loadingAccount: boolean;
    @mobx.observable
    private eventId: number;

    private subscriptions: Unsubscribe[];

    constructor(
        @IoC.Inject private readonly accountsDAO: IAccountsDAO,
        @IoC.Inject private readonly routingService: IRouter<RouteParams>
    ) {

    }

    public didMount() {
        this.subscriptions = [];

        this.subscriptions.push(this.routingService.listen(Routes.EventsList, (params: { accountId: string }) => {
            this.setPage(EventsModulePage.Events);
            this.fetchAccount(params.accountId);
        }));

        this.subscriptions.push(this.routingService.listenAll([
            Routes.EventManage,
            Routes.Meetings,
            Routes.OrganizationsList,
            Routes.AttendeesList,
            Routes.PassportEdit,
            Routes.RoomsList,
            Routes.ConnectionsList,
            Routes.Assets,
            Routes.Artifacts,
            Routes.Reports,
            Routes.Control,
        ], (params: { accountId: string; eventId: string }) => {
            mobx.runInAction(() => {
                this.setPage(EventsModulePage.ManageEvent);
                this.eventId = parseInt(params.eventId, RADIX_PARAM);
            });
        }));
    }

    public getPage() {
        return this.eventModulePage;
    }

    public isLoadingAccount() {
        return this.loadingAccount;
    }

    public willUnmount() {
        this.subscriptions.forEach(x => x());
    }

    public getAccount() {
        return this.account as DeepReadonly<IAccountDetailsDTO>;
    }

    public getEventId() {
        return this.eventId;
    }
    public getAccountId(): number {
        return parseInt(this.routeParams.accountId, RADIX_PARAM);
    }

    private get routeParams(): RouteParams[Routes.EventsList] {
        return this.routingService.router.getState().params;
    }

    @mobx.action
    private setPage(page: EventsModulePage) {
        this.eventModulePage = page;
    }

    @mobx.action
    private async fetchAccount(accountIdString: string) {
        const accountId = parseInt(accountIdString, RADIX_PARAM);

        if (!this.account || accountId !== this.account.accountId) {
            this.loadingAccount = true;
            let account;

            try {
                account = await this.accountsDAO.readAccount(accountId);
            } catch (error) {
                this.routingService.navigate(Routes.AccountsList, {});
            }

            if (account) this.setAccount(account);
        }
    }

    @mobx.action
    private setAccount(account: IAccountDetailsDTO) {
        this.loadingAccount = false;
        this.account = account;
    }
}

export { EventsVM, EventsModulePage };
